import React, { useEffect, useState } from "react";
import PageSeo from "../../components/seo/PageSeo";
import { Navigate } from "react-router-dom";
import { orderListCall, cancelCall, finishCall } from "../../calls";
import { useTranslation } from "react-i18next";
import "./orderList.css";
import Error from "../../components/Error/Error";
import Flag from "../../components/Flag";
import { countryName, fmtDateTime } from "../../utils/i18nFormat";
import { orderActionError } from "../../utils/serverMessages";

/* ORDER LIST — design "Signal" 4c */

const STATUS = {
  "sms pending": { cls: "sg-status--warn", key: "signal.pending" },
  success: { cls: "sg-status--ok", key: "signal.received" },
  finished: { cls: "sg-status--ok", key: "signal.ord-finished" },
  cancelled: { cls: "sg-status--err", key: "signal.ord-cancelled" },
  expired: { cls: "sg-status--mut", key: "signal.ord-expired" },
};

const OrderList = (props) => {
  const {
    setIsLoggedIn, isLoggedIn, setBalance,
    showErrorModal, setShowErrorModal, errorMsg, setErrorMsg,
    loader, setLoader,
  } = props;
  const { t, i18n } = useTranslation();

  const [orderListData, setOrderListData] = useState({ balance: 0, orders: [] });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const listOrders = () => {
    setLoader(false);
    orderListCall()
      .then((response) => {
        setLoader(true);
        setOrderListData(response.data);
        setBalance(response.data.balance);
      })
      .catch((err) => {
        setLoader(true);
        if (err.response && err.response.status === 401) {
          setIsLoggedIn(false);
          localStorage.removeItem("token");
        }
        setShowErrorModal(true);
        // Pas le message technique d'axios ("Request failed with status code 500")
        setErrorMsg([t("signal.t-try-again")]);
      });
  };

  useEffect(() => {
    listOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const executeCall = (id, callback) => {
    setLoader(false);
    callback(id)
      .then((response) => {
        setLoader(true);
        if (response.data.balance) setBalance(response.data.balance);
        listOrders();
      })
      .catch((err) => {
        setLoader(true);
        if (err.response && err.response.status === 401) {
          setIsLoggedIn(false);
          localStorage.removeItem("token");
        }
        setShowErrorModal(true);
        // Messages fixes du serveur ("Order is already successful"...) traduits
        setErrorMsg([orderActionError(err, t)]);
      });
  };

  if (!isLoggedIn) return <Navigate replace to="/" />;

  const orders = orderListData.orders
    .filter((o) => statusFilter === "all" || o.status === statusFilter)
    .filter((o) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        String(o.id).includes(q) ||
        (o.number || "").toLowerCase().includes(q) ||
        (o.service || "").toLowerCase().includes(q) ||
        (o.country || "").toLowerCase().includes(q) ||
        countryName(o.country_code, o.country, i18n.language).toLowerCase().includes(q)
      );
    });

  return (
    <div className="sg-page">
      <PageSeo noindex title={t("overview.order-history")} />
      <div className="sg-panel">
        <div className="sg-head">
          <h1 className="sg-h1">{t("overview.order-history")}</h1>
          <span style={{ font: "400 13px var(--font-body)", color: "var(--color-muted)" }}>
            {t("signal.ord-count", { count: orderListData.orders.length })}
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <div className="sg-search" style={{ flex: "1 1 220px" }}>
            <input
              type="search"
              placeholder={t("signal.ord-search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select
            className="sg-pager__b"
            style={{ height: 38 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t("signal.ord-all-statuses")}</option>
            <option value="sms pending">{t("signal.pending")}</option>
            <option value="success">{t("signal.received")}</option>
            <option value="finished">{t("signal.ord-finished")}</option>
            <option value="cancelled">{t("signal.ord-cancelled")}</option>
            <option value="expired">{t("signal.ord-expired")}</option>
          </select>
        </div>

        {loader && (
          <div className="sg-table" style={{ "--cols": "64px 110px minmax(90px,1fr) 100px minmax(130px,1fr) 80px 100px 90px" }}>
            <div className="sg-tr sg-tr--head">
              <span>{t("overview.id")}</span>
              <span>{t("overview.date")}</span>
              <span>{t("overview.country")}</span>
              <span>{t("overview.service")}</span>
              <span>{t("overview.number")}</span>
              <span>{t("signal.ord-code")}</span>
              <span>{t("overview.status")}</span>
              <span className="sg-td--end">{t("signal.ord-action")}</span>
            </div>
            {orders.length === 0 ? (
              <div className="sg-empty" style={{ border: "none", borderRadius: 0 }}>{t("signal.ord-none")}</div>
            ) : (
              orders.map((item, index) => {
                const st = STATUS[item.status] || { cls: "sg-status--mut", key: null };
                const stLabel = st.key ? t(st.key) : item.status;
                const d = new Date(item.created_at);
                const code = (item.sms_codes || []).toString();
                const canCancel = item.status === "sms pending";
                const canFinish = item.status === "success";
                return (
                  <div key={index} className="sg-tr">
                    <span className="sg-td--mono">{item.id}</span>
                    <span className="sg-td--mono">
                      {fmtDateTime(d, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, i18n.language)}
                    </span>
                    <span className="sg-td--ellip" style={{ textTransform: "capitalize" }}>
                      <Flag code={item.country_code} /> {countryName(item.country_code, item.country, i18n.language)}
                    </span>
                    <span className="sg-td--ellip" style={{ textTransform: "capitalize" }}>{String(item.service || "").toLowerCase() === "other" ? t("signal.service-other") : item.service}</span>
                    <span className="sg-td--num">{item.number}</span>
                    {code ? (
                      <span className="sg-code-ok">{code}</span>
                    ) : (
                      <span className="sg-td--mono" style={{ color: "var(--color-muted)" }}>···</span>
                    )}
                    <span className={`sg-status ${st.cls}`}>{stLabel}</span>
                    <span className="sg-td--end">
                      {canCancel && (
                        <button className="sg-copy" onClick={() => executeCall(item.id, cancelCall)}>{t("signal.cancel")}</button>
                      )}
                      {canFinish && (
                        <button className="sg-copy" onClick={() => executeCall(item.id, finishCall)}>{t("signal.finish")}</button>
                      )}
                      {!canCancel && !canFinish && <span style={{ color: "var(--color-muted)" }}>—</span>}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      <Error
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
        errorMsg={errorMsg}
      />
    </div>
  );
};

export default OrderList;
